import type { ReactNode } from "react";

import { SectionKicker } from "../MarketingSection";

const FILE_OUTLINE = (
  <>
    <path d="M10 3h29l15 15v55H10z" />
    <path d="M39 3v15h15" />
  </>
);

export const AUDIO_FILE = (
  <>
    {FILE_OUTLINE}
    <path d="M25 51V34l18-4v17" />
    <circle cx="22" cy="53" r="6" />
    <circle cx="40" cy="49" r="6" />
  </>
);

export const LYRICS_FILE = (
  <>
    {FILE_OUTLINE}
    <path d="M20 34h24M20 43h24M20 52h18" />
  </>
);

export function FileIcon({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 64 76"
      aria-hidden
      fill="none"
      stroke="rgba(207,216,255,0.72)"
      strokeWidth={4}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-24 w-20 overflow-visible max-[680px]:h-[70px] max-[680px]:w-[58px]"
    >
      {children}
    </svg>
  );
}

type Side = { icons: ReactNode; label: ReactNode };

type CatalogEquationProps = {
  kicker: string;
  title: string;
  body: string;
  left: Side;
  right: Side;
};

function EquationSide({ side }: { side: Side }) {
  return (
    <div className="flex min-w-0 flex-col items-center justify-center gap-[18px] text-center">
      <div className="flex items-center justify-center gap-3 max-[680px]:gap-1">
        {side.icons}
      </div>
      <p className="m-0 whitespace-nowrap font-figtree text-[18px] font-black uppercase leading-none tracking-[0.02em] text-white max-[680px]:text-[13px] [&_em]:not-italic [&_em]:text-teal-500">
        {side.label}
      </p>
    </div>
  );
}

/** Navy band with an "x in = y out" graphic. */
export default function CatalogEquation({
  kicker,
  title,
  body,
  left,
  right,
}: CatalogEquationProps) {
  return (
    <section className="px-[6vw] py-[100px] text-white [background:radial-gradient(circle_at_80%_30%,rgba(62,80,255,0.5),transparent_30%),#050B2D] max-[680px]:px-5 max-[680px]:py-[76px]">
      <div className="mx-auto grid w-[min(1180px,100%)] grid-cols-[0.85fr_1.15fr] items-center gap-[70px] max-[980px]:grid-cols-1">
        <div>
          <SectionKicker className="!text-teal-500">{kicker}</SectionKicker>
          <h2 className="m-0 font-figtree text-[clamp(42px,5vw,67px)] font-black uppercase leading-none tracking-[-0.05em]">
            {title}
          </h2>
          <p className="mt-[25px] max-w-[700px] font-open-sans text-[19px] leading-[1.65] text-white/70">
            {body}
          </p>
        </div>
        <div
          aria-hidden
          className="grid min-h-[310px] grid-cols-[minmax(145px,1fr)_auto_minmax(220px,1.25fr)] items-center gap-7 rounded-[28px] border border-white/[0.16] bg-[linear-gradient(135deg,rgba(255,255,255,0.07),rgba(84,61,255,0.11))] px-11 py-[38px] shadow-[inset_0_1px_rgba(255,255,255,0.04)] max-[980px]:max-w-[520px] max-[680px]:min-h-[250px] max-[680px]:w-full max-[680px]:grid-cols-[minmax(88px,1fr)_auto_minmax(142px,1.35fr)] max-[680px]:gap-3 max-[680px]:px-[18px] max-[680px]:py-7"
        >
          <EquationSide side={left} />
          <i className="font-figtree text-[52px] font-black not-italic leading-none text-[rgba(207,216,255,0.8)] max-[680px]:text-[36px]">
            =
          </i>
          <EquationSide side={right} />
        </div>
      </div>
    </section>
  );
}
